<template>
    <TooltipComp class="w-100">
        <template #tooltipcontent>
            <div class="d-flex flex-column">
                <div
                    v-for="stationRole in stationRoles"
                    :key="stationRole.ID"
                >
                    {{ getStationRoleName(stationRole) }}
                </div>
                <div
                    v-if="deviceStatus.IsActiveEvent"
                    class="mt-3"
                ></div>
                <div
                    v-for="device in deviceStatuses"
                    :key="device.DeviceStatus?.DeviceId ?? ''"
                    class="d-flex gap-2"
                >
                    <span>{{ device.DeviceStatus?.DeviceId }}</span>
                    <span>{{ device.Station?.Name }}</span>
                    <span :class="device.DeviceStatus?.Connected ? 'text-success' : 'text-danger'">
                        {{ $tm(device.DeviceStatus?.Connected ? 'status.online' : 'status.offline') }}
                    </span>
                </div>
            </div>
        </template>
        <StandardHeader
            :displayName
            :grid
            :column
            :classes="classes"
        >
            <slot></slot>
        </StandardHeader>
    </TooltipComp>
</template>
<script setup lang="ts" generic="TRowData">
import type { IGridCompExposed } from '@/components/Grid/Classes';
import type { IGridStandardHeaderParams } from '@/components/Grid/Classes/IGridStandardHeaderParams';
import type { IHeaderComponentParamsColumn } from '@/components/Grid/Classes/params/IHeaderComponentParamsColumn';
import { StandardHeader } from '@/components/Grid';
import { TooltipComp } from '@/components';
import type { StationRoleId } from '@/utilities/typealiases';
import { useEventCommonStore } from '@/stores';
import { computed } from 'vue';
import { StationRole } from '@/objects/generated';
import { useDeviceStatus } from '@/composeables/devicestatus/devicestatus';
import type IMatrixConfig from '../classes/IMatrixConfig';
import { $tm } from '@/locize';
import { arrayUniqueWithComparer } from '@/utilities/arrayUtils';

const props = defineProps<
    {
        grid: IGridCompExposed<TRowData>;
        column: IHeaderComponentParamsColumn;
        stationRoleIds: StationRoleId[];
        matrixConfig: IMatrixConfig;
    } & IGridStandardHeaderParams
>();

const eventCommonStore = useEventCommonStore();

const stationRoles = computed<StationRole[]>(() => props.stationRoleIds.map((srid) => eventCommonStore.StationRolesMap.get(srid)!));

const deviceStatus = useDeviceStatus();

/**
 * Gets the name of a stationRole
 * If we have multiple races displayed, we add the racename to the stationRole
 * @param stationRole The stationRole to get the name of
 */
const getStationRoleName = (stationRole: StationRole) => {
    let name = stationRole.Name;

    // If its multirace we add the racename to the rolename
    if ((props.matrixConfig.RaceIDs?.length ?? 0) > 1) {
        name += ' ';
        if (stationRole.RaceID === null) {
            // No raceId is for ALL races
            name += '(' + $tm('general.all') + ')';
        } else {
            // Add the name of the specific race
            name += '(' + (eventCommonStore.RacesMap.get(stationRole.RaceID)?.Name ?? '') + ')';
        }
    }

    return name;
};

/**
 * Find the devicestatuses for this stationrole
 */
const deviceStatuses = computed(() => {
    if (!deviceStatus.IsActiveEvent) {
        return [];
    }

    return arrayUniqueWithComparer(
        stationRoles.value.flatMap((sr) => deviceStatus.EventDeviceStatuses.filter((ds) => ds.Station?.ID === sr.StationID)),
        (a, b) => a.DeviceStatus?.DeviceId === b.DeviceStatus?.DeviceId,
    );
});

/**
 * The status dots in the header is css only, and the classes come from here
 */
const classes = computed(
    () =>
        'matrix-header ' +
        deviceStatuses.value.map((ds, i) => `station-${i + 1}-${ds.DeviceStatus?.Connected ? 'green' : 'red'} `).join(' '),
);
</script>
